import { ComponentProps, useCallback, useMemo, useState } from 'react';
import RadioGroup from './RadioGroup';

type RadioGroupProps = ComponentProps<typeof RadioGroup>;

const useRadioGroupOptions = (initialOptions: RadioGroupProps['options'], defaultValue?: string) => {
  const [options, setOptions] = useState(initialOptions);

  const onChange: RadioGroupProps['onChange'] = useCallback(
    value => {
      const newOptions = options.map(option => {
        const selected = option.value === value; // Only select the "changed" option
        return { ...option, selected };
      });
      setOptions(newOptions);
    },
    [options]
  );

  const selectedOption = useMemo(() => {
    return options.find(option => option.selected);
  }, [options]);

  const clearOptions = useMemo(() => {
    if (selectedOption?.value === defaultValue) {
      return undefined;
    }
    return () => {
      const clearedOptions = options.map(option => ({ ...option, selected: option.value === defaultValue }));
      setOptions(clearedOptions);
    };
  }, [defaultValue, options, selectedOption?.value]);

  return useMemo(() => ({ clearOptions, onChange, options, selectedOption, setOptions }), [clearOptions, onChange, options, selectedOption]);
};

export default useRadioGroupOptions;
